import React from 'react';
import { Card } from '../ui/Card';
import { Message, Task } from '../../types';
import { format, subDays } from 'date-fns';
import { motion } from 'framer-motion';
import { Activity, Clock, Award } from 'lucide-react';

interface KeyInsightsProps {
  messages: Message[];
  tasks: Task[];
}

export const KeyInsights: React.FC<KeyInsightsProps> = ({ messages, tasks }) => {
  // Calculate peak activity times
  const getPeakActivity = () => {
    const today = new Date();
    const lastWeek = subDays(today, 7);
    const recentMessages = messages.filter(m => new Date(m.createdAt) > lastWeek);
    const recentTasks = tasks.filter(t => new Date(t.createdAt) > lastWeek);
    
    const totalActivity = recentMessages.length + recentTasks.length;
    return `${totalActivity} activities in the last 7 days`;
  };

  // Calculate average response time
  const getResponseTime = () => {
    const acknowledgedMessages = messages.filter(m => m.acknowledgments.length > 0);
    if (acknowledgedMessages.length === 0) return 'No responses yet';

    const totalResponses = acknowledgedMessages.length;
    const responseRate = Math.round((totalResponses / messages.length) * 100);
    return `${responseRate}% response rate`;
  };

  // Calculate top performer
  const getTopPerformer = () => {
    const completedTasks = tasks.filter(t => t.status === 'COMPLETED');
    if (completedTasks.length === 0) return 'No completed tasks yet';

    const completionRate = Math.round((completedTasks.length / tasks.length) * 100);
    return `${completionRate}% task completion rate`;
  };

  return (
    <Card className="p-6">
      <h2 className="text-lg font-semibold mb-4">Key Insights</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg"
        >
          <div className="flex items-center gap-2 mb-2">
            <Activity className="w-5 h-5 text-blue-600" />
            <h3 className="font-medium text-blue-700">Peak Activity</h3>
          </div>
          <p className="text-sm text-blue-600">{getPeakActivity()}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="p-4 bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-lg"
        >
          <div className="flex items-center gap-2 mb-2">
            <Clock className="w-5 h-5 text-emerald-600" />
            <h3 className="font-medium text-emerald-700">Response Time</h3>
          </div>
          <p className="text-sm text-emerald-600">{getResponseTime()}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="p-4 bg-gradient-to-br from-violet-50 to-violet-100 rounded-lg"
        >
          <div className="flex items-center gap-2 mb-2">
            <Award className="w-5 h-5 text-violet-600" />
            <h3 className="font-medium text-violet-700">Top Performer</h3>
          </div>
          <p className="text-sm text-violet-600">{getTopPerformer()}</p>
        </motion.div>
      </div>
    </Card>
  );
};